import Cookies from "universal-cookie";
import CryptoJS from "crypto-js";
import SecureStorage from "secure-web-storage";
import * as jwt from "jsonwebtoken";

import {
  TOKEN_NAMES,
  TOKEN_EXPIRY,
  PRACTICE_EDITION_WHITELABEL,
} from "../constants/constants";
import {
  IV_KEY,
  NEXTGEN_EHR_WHITELABEL,
  VERADIGM_EHR_WHITELABEL,
  SECRET_KEY,
  LIVECHAT_DOMAIN,
  CERNER_EHR_WHITELABEL,
} from "../constants/constants";
import TelevoxBackground from "../images/login/televox-bg.png";
import TelevoxLogo from "../images/login/televox-logo.svg";
import CernerBackground from "../images/login/cerner-bg.png";
import NextgenBackground from "../images/login/nextgen_background.jpg";
import VeradigmBackground from "../images/login/veradigm-background.png";
import VeradigmLogo from "../images/login/veradigm-logo.png";
import NextgenLogo from "../images/login/nextgen-logo.png";
import PracticeEditionBackground from "../images/login/practice-edition-bg-logo.png";
import PracticeEditionLogo from "../images/logos/practiceEdition/practice-edition-logo-televox.png";
import CernerLogo from "../images/logos/cerner-logo.png";
import { prop } from "ramda";
import FileSaver from "file-saver";
import loginService from "../service/login";

const cookies = new Cookies();

export const DASH = "-";
const MAX_AGE = 60 * 60 * 3; // 3 hours
const defaultCookieOptions = {
  path: "/",
  sameSite: "none",
  secure: true,
};

export const saveTokenMs = (token) => {
  const decodedToken = jwt.decode(token, { complete: true });
  if (decodedToken) {
    setEncryptedCookie(TOKEN_NAMES.FE, decodedToken.payload);
    saveTokenData(decodedToken);
  }
};

export const hasToken = () => {
  const sessionValue = getTokenExpiry();
  return (
    (sessionValue && new Date().getTime() < parseInt(sessionValue)) ||
    checkMSLogin()
  );
};

export const hasMsInfoToken = () => {
  const msInfo = getCookie("ms-lc-info");

  if (msInfo) {
    removeCookie(
      "ms-lc-info",
      process.env.REACT_APP_ENV !== "dev" && { domain: LIVECHAT_DOMAIN },
      false,
    );
    return msInfo;
  }

  return "";
};

export const checkMSLogin = () => {
  const msToken = getCookie(TOKEN_NAMES.MS);
  const msLogin = getCookie(TOKEN_NAMES.MS_LOGIN);

  if (msToken && msLogin) {
    saveTokenMs(msToken);
    removeCookie(
      TOKEN_NAMES.MS,
      process.env.REACT_APP_ENV !== "dev" && { domain: LIVECHAT_DOMAIN },
      false,
    );

    return true;
  } else {
    return false;
  }
};

export const getSortedStringValues = (a, b) => {
  const firstValue = a ? a.toString().trimStart() : DASH;
  const secondValue = b ? b.toString().trimStart() : DASH;
  if (firstValue.toLowerCase() > secondValue.toLowerCase()) {
    return 1;
  }
  if (a === DASH) {
    return 1;
  }
  if (b === DASH) {
    return -1;
  }
  return -1;
};
export const downloadCSV = (data, fileName = "sample.csv") => {
  const csvData = Array.isArray(data)
    ? data.reduce((result, row) => {
        let lineStr = row;
        if (Array.isArray(row)) {
          lineStr = row.join(",");
        }
        const tempRes = `${result}${lineStr} \r\n`;
        return tempRes;
      }, "")
    : data;
  const blob = new Blob([csvData], { type: "text/csv" });
  FileSaver.saveAs(blob, fileName);
};
export const dummyRequest = (fileFormats = []) => ({
  file,
  onSuccess,
  onProgress,
  onError,
}) => {
  let progress = 0;
  const progressLoader = setInterval(() => {
    onProgress({ percent: progress });
    progress += 10;
  }, 100);
  setTimeout(() => {
    clearInterval(progressLoader);
    const parts = file.name.split(".");
    const extension = parts[parts.length - 1];
    if (fileFormats.includes(extension)) onSuccess("ok");
    else onError("Invalid file format");
  }, 1500);
};

export const getTokenExpiry = () => {
  const expiry = cookies.get("tokenExpiry");
  return expiry ? parseInt(expiry) : undefined;
};

export const saveToken = (token) => {
  const cookieOptions = {
    ...defaultCookieOptions,
    maxAge: MAX_AGE,
  };
  cookies.set(TOKEN_NAMES.FE, token, cookieOptions);
  cookies.set(
    "tokenExpiry",
    new Date().getTime() + MAX_AGE * 1000,
    cookieOptions,
  );
};

export const saveTokenData = (tokenData) => {
  const cookieOptions = {
    ...defaultCookieOptions,
    maxAge: MAX_AGE,
  };

  setEncryptedCookie(TOKEN_EXPIRY, new Date().getTime() + MAX_AGE * 1000);
  if (tokenData) {
    const { email, name } = tokenData;
    setCookie("sessionEmail", email, cookieOptions);
    setCookie("sessionUserName", name, cookieOptions);
  }
};

export const removeCookie = (
  name,
  options = {},
  includeDefaultOptions = true,
) => {
  cookies.remove(name, {
    ...(includeDefaultOptions ? defaultCookieOptions : {}),
    ...options,
  });
};

export const setCookie = (name, value, options = {}) => {
  cookies.set(name, value, {
    ...defaultCookieOptions,
    ...options,
  });
};
export const getCookieKey = (name) => {
  const keyUTF = CryptoJS.enc.Base64.parse(SECRET_KEY);
  const cookieKey = CryptoJS.SHA256(name, keyUTF).toString(CryptoJS.enc.Base64);
  return cookieKey.replace(/=/g, "-");
};
export const getCookie = (name, options = {}, includeDefaultOptions = true) => {
  return cookies.get(name, {
    ...(includeDefaultOptions ? defaultCookieOptions : {}),
    ...options,
  });
};
export const removeEncryptedCookie = (
  name,
  nameEncrypted = false,
  options = defaultCookieOptions,
  includeDefaultOptions = true,
) => {
  const cookieKey = nameEncrypted ? getCookieKey(name) : name;
  removeCookie(cookieKey, options, includeDefaultOptions);
};
export const setEncryptedCookie = (
  name,
  value,
  encryptName = false,
  options = {},
) => {
  const keyUTF = CryptoJS.enc.Base64.parse(SECRET_KEY);
  const iv = CryptoJS.enc.Base64.parse(IV_KEY);
  const cookieKey = encryptName ? getCookieKey(name) : name;
  const encryptedValue = CryptoJS.AES.encrypt(JSON.stringify(value), keyUTF, {
    iv,
  }).toString();
  setCookie(cookieKey, encryptedValue, {
    ...defaultCookieOptions,
    ...options,
  });
};
export const getEncryptedCookie = (
  name,
  nameEncrypted = false,
  options = {},
) => {
  const key = nameEncrypted ? getCookieKey(name) : name;
  const keyUTF = CryptoJS.enc.Base64.parse(SECRET_KEY);
  const iv = CryptoJS.enc.Base64.parse(IV_KEY);
  const encryptedValue = getCookie(key, {
    ...defaultCookieOptions,
    ...options,
  });
  let decryptedValue;
  if (encryptedValue) {
    decryptedValue = CryptoJS.AES.decrypt(
      encryptedValue.toString(CryptoJS.enc.Base64),
      keyUTF,
      {
        iv,
      },
    ).toString(CryptoJS.enc.Utf8);
    decryptedValue = JSON.parse(decryptedValue || "");
  }
  return decryptedValue || "";
};

export const secureStorage = new SecureStorage(localStorage, {
  hash: function hash(key) {
    key = CryptoJS.SHA256(key, SECRET_KEY);

    return key.toString();
  },
  encrypt: function encrypt(data) {
    data = CryptoJS.AES.encrypt(data, SECRET_KEY);

    data = data.toString();

    return data;
  },
  decrypt: function decrypt(data) {
    data = CryptoJS.AES.decrypt(data, SECRET_KEY);

    data = data.toString(CryptoJS.enc.Utf8);

    return data;
  },
});

export const getStandardTime = (time) => {
  const timeArray = time.split(":"); // convert to array
  // fetch
  const hours = Number(timeArray[0]);
  const minutes = Number(timeArray[1]);
  // calculate
  let timeValue;
  if (hours > 0 && hours <= 12) {
    timeValue = `${hours < 10 ? "0" : ""}${hours}`;
  } else if (hours > 12) {
    timeValue = `${hours - 12 < 10 ? "0" : ""}${hours - 12}`;
  } else if (hours === 0) {
    timeValue = "12";
  }
  timeValue += minutes < 10 ? ":0" + minutes : ":" + minutes; // get minutes
  timeValue += hours >= 12 ? " PM" : " AM";
  return timeValue;
};

export const getIconColor = () => {
  if (NEXTGEN_EHR_WHITELABEL) {
    return "#c76e4d";
  } else if (CERNER_EHR_WHITELABEL) {
    return "#8b91c8";
  } else if (PRACTICE_EDITION_WHITELABEL) {
    return "#27557e";
  } else if (VERADIGM_EHR_WHITELABEL) {
    return "#403c7e";
  } else {
    return "#00B4E5";
  }
};
export const getFormData = (object) =>
  Object.keys(object).reduce((formData, key) => {
    formData.append(key, object[key]);
    return formData;
  }, new FormData());

export const dummyRequestAudio = (fileFormats = [], maxFileSize) => ({
  file,
  onSuccess,
  onProgress,
  onError,
}) => {
  let progress = 0;
  const progressLoader = setInterval(() => {
    onProgress({ percent: progress });
    progress += 10;
  }, 100);
  setTimeout(() => {
    clearInterval(progressLoader);
    const parts = file.name.split(".");
    const extension = getLastElement(parts).toLowerCase();
    let error = "";
    if (fileFormats.includes(extension)) {
      if (maxFileSize && file.size > maxFileSize) {
        error = "File exceeds max limit";
      } else if (file.size === 0) {
        error = "File is too short ";
      } else {
        onSuccess("ok");
      }
    } else {
      error = "Unsupported file format";
    }
    if (error) {
      onError(error);
    }
  }, 1500);
};
const getLastElement = (array = []) => {
  return array.slice(-1)[0];
};

export const convertBytes = (val) => {
  let base = 0,
    parsedVal = parseInt(val, 10) || 0;
  for (; 1024 <= parsedVal && ++base; ) parsedVal /= 1024;
  return (
    parsedVal.toFixed(10 > parsedVal && 0 < base ? 1 : 0) +
    " " +
    ["bytes", "KB", "MB"][base]
  );
};
export const getLogoBasedOnEHR = () => {
  if (NEXTGEN_EHR_WHITELABEL) {
    return NextgenLogo;
  } else if (CERNER_EHR_WHITELABEL) {
    return CernerLogo;
  } else if (PRACTICE_EDITION_WHITELABEL) {
    return PracticeEditionLogo;
  } else if (VERADIGM_EHR_WHITELABEL) {
    return VeradigmLogo;
  } else return TelevoxLogo;
};

export const getBGBasedOnEHR = () => {
  if (NEXTGEN_EHR_WHITELABEL) {
    return NextgenBackground;
  } else if (CERNER_EHR_WHITELABEL) {
    return CernerBackground;
  } else if (PRACTICE_EDITION_WHITELABEL) {
    return PracticeEditionBackground;
  } else if (VERADIGM_EHR_WHITELABEL) {
    return VeradigmBackground;
  } else return TelevoxBackground;
};

export const getLabelBasedOnEHR = () => {
  if (NEXTGEN_EHR_WHITELABEL) {
    return "Nextgen";
  } else if (CERNER_EHR_WHITELABEL) {
    return "Cerner";
  } else if (PRACTICE_EDITION_WHITELABEL) {
    return "Practice Edition";
  } else if (VERADIGM_EHR_WHITELABEL) {
    return "veradigm";
  } else return "Housecalls Pro";
};

export const getDescriptionBasedOnEHR = () => {
  if (PRACTICE_EDITION_WHITELABEL) {
    return "Patient Relationship Management Platform";
  } else {
    return "Modernize your digital patient outreach and drive communications from your EHR.";
  }
};

export const getFaviconBasedOnEHR = () => {
  if (NEXTGEN_EHR_WHITELABEL) {
    return "/nextgen-favicon.ico";
  } else if (CERNER_EHR_WHITELABEL) {
    return "/cerner_favicon.ico";
  } else if (VERADIGM_EHR_WHITELABEL) {
    return "/veradigm_favicon.ico";
  } else return "/televox-favicon.ico";
};

export const generateId = (length, alphaOnly = false) => {
  var result = "";
  var characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz" +
    (alphaOnly ? "" : "0123456789");
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const getValueByKey = (objOrArray, key, defaultKey, defaultValue) => {
  let value = defaultValue;
  if (objOrArray?.hasOwnProperty(key)) value = prop(key, objOrArray);
  else if (defaultKey) value = prop(defaultKey, objOrArray);
  return value;
};

export const debounceAsync = (func, wait, immediate) => {
  let timeout;

  return function executedFunction() {
    const context = this;
    const args = arguments;

    return new Promise((resolve, reject) => {
      const later = async function() {
        timeout = null;
        try {
          if (!immediate) {
            const response = await func.apply(context, args);
            resolve(response);
          }
        } catch (err) {
          reject(err);
        }
      };

      const callNow = immediate && !timeout;

      clearTimeout(timeout);

      timeout = setTimeout(later, wait);

      if (callNow)
        func
          .apply(context, args)
          .then(resolve)
          .catch(reject);
    });
  };
};

export const ellipsize = (text, length) => {
  return text
    ? text.substr(0, length) + (text.length > length ? "..." : "")
    : "";
};

export const escapeRegExp = (value = "") =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const replaceFieldVariables = (
  message = "",
  headers = [],
  getReplacement = () => "",
) => {
  if (!message || !Array.isArray(headers) || headers.length === 0) {
    return message;
  }

  return headers.reduce((formattedMessage, header) => {
    const fieldVariable = header?.Header;

    if (!fieldVariable) {
      return formattedMessage;
    }

    const replacement = getReplacement(header);

    if (replacement === undefined || replacement === null) {
      return formattedMessage;
    }

    const regex = new RegExp(escapeRegExp(`{${fieldVariable}}`), "g");

    return formattedMessage.replace(regex, replacement);
  }, message);
};

export const getFormattedMessage = (message, data, headers) => {
  if (!message) return "";
  if (!data?.length || !Array.isArray(headers) || headers.length === 0)
    return message;

  const specialReplacements = {
    SurveyLink: "{SurveyLink}",
    AgentChatLink: "{AgentChatLink}",
  };

  const formattedMessage = replaceFieldVariables(message, headers, (header) => {
    if (specialReplacements[header.Header] !== undefined) {
      return specialReplacements[header.Header];
    }

    if (
      data &&
      data[0] &&
      data[0][header.accessor] !== undefined &&
      data[0][header.accessor] !== null
    ) {
      return data[0][header.accessor];
    }

    return `{${header.Header}}`;
  });

  return formattedMessage.replace(
    /{Account Name}/g,
    loginService.getAccountName(),
  );
};
